import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as React from "react";
import { useDialogGuard } from "@/hooks/use-dialog-guard";
import { cn } from "@/lib/utils";
import { optionHoverClass } from "./option-row";

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, onValueChange, ...props }, ref) => {
  const { markDirty } = useDialogGuard();

  // QL-119 — rows come from `OptionRow`, whose min-height already provides the
  // rhythm between options; shadcn's default `gap-2` would double-space them.
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid gap-0", className)}
      onValueChange={(value) => {
        markDirty();
        onValueChange?.(value);
      }}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

/**
 * Design 14g — a 16px ring that never changes fill; selection is an 8px dot
 * drawn inside it. Bare option controls sit on the page with nothing around
 * them to carry a focus ring, so unlike the field chassis they take ring-2 with
 * an offset.
 *
 * `peer` is what lets a following `Label` see the disabled state, the same way
 * Checkbox and Switch do it. Without it a disabled option keeps full-strength
 * text under a `cursor: pointer`, which reads as clickable.
 */
const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        optionHoverClass,
        "peer aspect-square h-4 w-4 shrink-0 rounded-full border border-primary text-primary shadow-sm transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex h-full w-full items-center justify-center">
        <span className="block h-2 w-2 rounded-full bg-primary" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
